import { useEffect, useContext, useMemo, useState, useRef } from 'react';
import { CommonDataContext } from '../../firebase/CommonDataContext';
import { FirebaseNewContext } from '../../firebase/FirebaseNewContext';
import { GlobalSettingsContext } from '../../firebase/GlobalSettingsContext';
import { UserDataContext } from '../../firebase/UserDataContext';
import { groupedEventsScores, avgOverallInEvent, avgByCategoryInEvent, avgByDivisionInEvent, getOverallTopInEvent, getCategoryTopOverallInEvent, getEventCategoryPlaceInEvent, getPlacementOverallInEvent, getPlacementOverAllEventsAndCategories, calculatePlacementsAsMap } from '../../components/misc/Utils';

export default function useResultsV2Logic() {
  const { defaultSelectedCompetition } = useContext(UserDataContext);
  const { firebase } = useContext(FirebaseNewContext);
  const { eventsMap: globalEventsMap, teamsMap: globalTeamsMap } = useContext(CommonDataContext);
  const { globalSettings } = useContext(GlobalSettingsContext);

  const [eventsScores, setEventsScores] = useState({});
  const [teamsMapLocal, setTeamsMapLocal] = useState({});
  const [loading, setLoading] = useState(true);

  const eventsMap = useMemo(() => globalEventsMap?.[defaultSelectedCompetition] || {}, [globalEventsMap, defaultSelectedCompetition]);
  const teams = useMemo(() => globalTeamsMap?.[defaultSelectedCompetition] || {}, [globalTeamsMap, defaultSelectedCompetition]);

  // helpers to produce deterministic ordering by numeric event/team number
  const eventNumberForUid = (uid) => {
    const evNum = (eventsScores && eventsScores[uid] && eventsScores[uid].event && eventsScores[uid].event.number) ?? (eventsMap && eventsMap[uid] && eventsMap[uid].number);
    return Number.isFinite(Number(evNum)) ? Number(evNum) : Infinity;
  };
  const sortEventUids = (uids) => (uids || []).slice().sort((a,b) => {
    const an = eventNumberForUid(a);
    const bn = eventNumberForUid(b);
    if (an !== Infinity && bn !== Infinity) return an - bn;
    const aName = (eventsScores && eventsScores[a] && eventsScores[a].event && eventsScores[a].event.name) || (eventsMap && eventsMap[a] && eventsMap[a].name) || '';
    const bName = (eventsScores && eventsScores[b] && eventsScores[b].event && eventsScores[b].event.name) || (eventsMap && eventsMap[b] && eventsMap[b].name) || '';
    return aName.localeCompare(bName);
  });

  const teamNumberForUid = (uid) => {
    const tn = (teamsMapLocal && teamsMapLocal[uid] && teamsMapLocal[uid].teamNumber) ?? (teams && teams[uid] && teams[uid].teamNumber);
    return Number.isFinite(Number(tn)) ? Number(tn) : Infinity;
  };
  const sortTeamUids = (uids) => (uids || []).slice().sort((a,b) => {
    const an = teamNumberForUid(a);
    const bn = teamNumberForUid(b);
    if (an !== Infinity && bn !== Infinity) return an - bn;
    const aName = (teamsMapLocal && teamsMapLocal[a] && teamsMapLocal[a].teamName) || (teams && teams[a] && teams[a].teamName) || '';
    const bName = (teamsMapLocal && teamsMapLocal[b] && teamsMapLocal[b].teamName) || (teams && teams[b] && teams[b].teamName) || '';
    return aName.localeCompare(bName);
  });

  const [teamModes, setTeamModes] = useState({});
  const [affiliatedMode, setAffiliatedMode] = useState('none'); // 'none' | 'average' | 'max'
  const [autoAppliedModes, setAutoAppliedModes] = useState({}); // uid -> previousMode
  const [manualOverrides, setManualOverrides] = useState({}); // uid -> true when user manually edits a team's mode
  const [tabIndex, setTabIndex] = useState(0);

  // Top-X / grouping controls
  const [topN, setTopN] = useState(0); // 0 == All
  const [groupBy, setGroupBy] = useState('competition'); // 'competition'|'event'|'category'|'division'
  const [includeTies, setIncludeTies] = useState(true);

  // new report selector state
  const [reportType, setReportType] = useState('dashboard');
  const [selectedEvent, setSelectedEvent] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDivision, setSelectedDivision] = useState('');

  // compute affiliated teams list (uids)
  const affiliatedTeamUids = useMemo(() => {
    if (!eventsScores) return [];
    const setU = new Set();
    Object.keys(eventsScores).forEach(evUid => {
      const ev = eventsScores[evUid];
      if (!ev || !ev.event) return;
      (ev.event.associatedTeams || []).forEach(a => setU.add(typeof a === 'string' ? a : a.uid));
    });
    return Array.from(setU);
  }, [eventsScores]);

  // categories and divisions from globalSettings
  const categoriesMap = useMemo(() => {
    if (globalSettings && Object.keys(globalSettings).length !== 0) {
      return (globalSettings['categories'] && globalSettings['categories'][defaultSelectedCompetition]) || {};
    }
    return {};
  }, [globalSettings, defaultSelectedCompetition]);

  const divisionsMap = useMemo(() => {
    if (globalSettings && Object.keys(globalSettings).length !== 0) {
      return (globalSettings['divisions'] && globalSettings['divisions'][defaultSelectedCompetition]) || {};
    }
    return {};
  }, [globalSettings, defaultSelectedCompetition]);

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      // If no competition selected, ensure we don't stay in a loading state
      if (!defaultSelectedCompetition) {
        setTeamsMapLocal({});
        if (mounted) {
          setEventsScores({});
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      try {
        setTeamsMapLocal(teams || {});
        const scoresMapResult = await firebase.getScores(defaultSelectedCompetition);
        const eventScoresMap = groupedEventsScores(scoresMapResult || {}, eventsMap || {}, teams || {});

        // compute event-level aggregates and placements (similar to ResultsPage v1)
        avgOverallInEvent(eventScoresMap);
        avgByCategoryInEvent(eventScoresMap, categoriesMap);
        avgByDivisionInEvent(eventScoresMap, divisionsMap);
        getOverallTopInEvent(eventScoresMap);
        getCategoryTopOverallInEvent(eventScoresMap, categoriesMap);
        // event-level associated placements will be recalculated by a separate effect that
        // considers current teamModes and affiliatedMode. For now set base values.
        const baseOptions = { affiliatedMode, teamModes, manualOverrides };
        getEventCategoryPlaceInEvent(eventScoresMap, categoriesMap, baseOptions);
        getPlacementOverallInEvent(eventScoresMap, baseOptions);

        // compute competition-level placements based on raw and associated totals
        // clone teams to avoid mutating context objects (Utils may mutate the map)
        const teamsClone = Object.fromEntries(Object.entries(teams || {}).map(([k, v]) => [k, { ...(v || {}) }]));
        getPlacementOverAllEventsAndCategories(eventScoresMap, teamsClone, categoriesMap, baseOptions);

        if (mounted) setEventsScores(eventScoresMap);

        // initialize teamModes for present teams using previous values when available
        if (mounted) {
          setTeamModes(prev => {
            const initial = {};
            Object.keys(teams || {}).forEach(t => { initial[t] = (prev && prev[t]) || 'normal'; });
            return initial;
          });
        }
      } catch (e) {
        console.error('ResultsV2 fetch error', e);
        if (mounted) setEventsScores({});
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, [firebase, defaultSelectedCompetition, eventsMap, teams, categoriesMap, divisionsMap]);

  // Recompute event-level associated placements when modes or eventsScores change
  useEffect(() => {
    if (!eventsScores || Object.keys(eventsScores).length === 0) return;
    try {
      // recompute aggregates (avg/top) in case eventsScores mutated
      avgOverallInEvent(eventsScores);
      avgByCategoryInEvent(eventsScores, categoriesMap);
      avgByDivisionInEvent(eventsScores, divisionsMap);
      getOverallTopInEvent(eventsScores);
      getCategoryTopOverallInEvent(eventsScores, categoriesMap);

      const options = { affiliatedMode, teamModes, manualOverrides };
      // pass modes so Utils will compute finalScoreAssociated according to current settings
      getEventCategoryPlaceInEvent(eventsScores, categoriesMap, options);
      getPlacementOverallInEvent(eventsScores, options);
      // recompute competition placements (use a cloned teams map to avoid mutating context)
      const teamsClone = Object.fromEntries(Object.entries(teams || {}).map(([k, v]) => [k, { ...(v || {}) }]));
      getPlacementOverAllEventsAndCategories(eventsScores, teamsClone, categoriesMap, options);

      // trigger update with an explicit clone to avoid closure surprises
      const cloned = { ...eventsScores };
      setEventsScores(cloned);
    } catch (e) {
      console.error('Error recalculating event placements', e);
    }
  }, [eventsScores, affiliatedMode, teamModes, categoriesMap, divisionsMap, teams, manualOverrides]);

  // Precompute event-level info (avg, max, associated teams and fast score lookup)
  const eventsInfo = useMemo(() => {
    const info = {};
    if (!eventsScores) return info;
    // iterate in deterministic order
    sortEventUids(Object.keys(eventsScores)).forEach(evUid => {
      const ev = eventsScores[evUid];
      if (!ev) return;
      const scoresArr = ev.scores || [];
      // filter out excluded/variable-max markers for max calculation
      const nonExcluded = scoresArr.filter(s => !s.excludeScore && !s.scoreMax);
      const evMax = (nonExcluded.length > 0)
        ? Math.max(...nonExcluded.map(s => Number(s.finalScore || 0)))
        : (scoresArr.length > 0 ? Math.max(...scoresArr.map(s => Number(s.finalScore || 0))) : 0);

      // build quick lookup map teamUid -> scoreObj
      const scoreByTeam = {};
      scoresArr.forEach(s => { if (s && s.teamUid) scoreByTeam[s.teamUid] = s; });

      const associatedUids = (ev.event?.associatedTeams || []).map(a => (typeof a === 'string' ? a : a.uid));
      const sortedScores = [...nonExcluded]
        .sort((a, b) => Number(a.finalScore || 0) - Number(b.finalScore || 0))
        .map(s => ({ teamUid: s.teamUid, score: Number(s.finalScore || 0) }));

      info[evUid] = {
        avg: Number(ev.avgOverall) || 0,
        max: Number.isFinite(evMax) ? evMax : 0,
        associated: associatedUids,
        scoreByTeam,
        sortedScores
      };
    });
    return info;
  }, [eventsScores]);

  // compute adjusted teams map
  const adjustedTeamsMap = useMemo(() => {
    const adjusted = {};
    if (!eventsScores || Object.keys(teamsMapLocal || {}).length === 0) return adjusted;

    const eventUids = sortEventUids(Object.keys(eventsScores));

    // helper: determine effective mode for a given team and event
    const getEffectiveMode = (tUid, isAffiliated) => {
      const perTeam = teamModes[tUid] || 'normal';
      const isManual = manualOverrides && manualOverrides[tUid];

      // explicit per-team modes that always apply
      if (perTeam === 'exclude') return 'exclude';
      if (perTeam === 'total_average') return 'total_average';
      if (perTeam && perTeam.startsWith('affiliated_')) return perTeam; // 'affiliated_average' or 'affiliated_max'

      // perTeam === 'normal'
      if (isManual) {
        // user explicitly set to normal -> do not apply global affiliated modes
        return 'normal';
      }

      // no per-team override -> apply global mode for affiliated events only
      if (isAffiliated) {
        if (affiliatedMode === 'average') return 'affiliated_average';
        if (affiliatedMode === 'max') return 'affiliated_max';
        if (affiliatedMode === 'up_next') return 'affiliated_up_next';
        return 'normal';
      }

      return 'normal';
    };

    // iterate teams and aggregate
    Object.keys(teamsMapLocal).forEach(tUid => {
      const team = teamsMapLocal[tUid] || {};
      const mode = teamModes[tUid] || 'normal';

      // initialize entry so teams with no submitted scores still appear
      adjusted[tUid] = {
        uid: tUid,
        teamNumber: team.teamNumber || '',
        teamName: team.teamName || '',
        category: team.category || '',
        division: team.division || '',
        affiliatedEventNames: [],
        affiliated: false,
        originalTotal: 0,
        adjustedTotal: 0,
        mode
      };

      let origTotal = 0;
      let adjTotal = 0;

      // loop over events using precomputed eventsInfo and fast lookup
      eventUids.forEach(evUid => {
        const ev = eventsScores[evUid];
        if (!ev) return;
        const evInfo = eventsInfo[evUid] || { avg: 0, max: 0, associated: [], scoreByTeam: {} };
        const scoreObj = evInfo.scoreByTeam[tUid];
        const eventAvg = evInfo.avg || 0;
        const eventMax = evInfo.max || 0;
        const isAffiliated = (evInfo.associated || []).includes(tUid);

        const effectiveMode = getEffectiveMode(tUid, isAffiliated);

        if (scoreObj) {
          const orig = Number(scoreObj.finalScore) || 0;
          origTotal += orig;

          switch (effectiveMode) {
            case 'total_average':
              adjTotal += eventAvg;
              break;
            case 'affiliated_average':
              adjTotal += (isAffiliated && orig < eventAvg) ? eventAvg : orig;
              break;
            case 'affiliated_max':
              adjTotal += (isAffiliated) ? eventMax : orig;
              break;
            case 'affiliated_up_next': {
              if (!isAffiliated) { adjTotal += orig; break; }
              const assoc = evInfo.associated || [];
              const next = (evInfo.sortedScores || []).find(e => e.score > orig && !assoc.includes(e.teamUid));
              adjTotal += next ? next.score : orig;
              break;
            }
            case 'exclude':
              adjTotal += (isAffiliated) ? 1500 : orig;
              break;
            case 'normal':
            default:
              adjTotal += orig;
          }
        } else {
          // missing submission
          if (effectiveMode === 'exclude' && isAffiliated) {
            adjTotal += 1500;
          } else {
            // no contribution for missing
          }
        }

        // collect affiliated event names regardless of whether team submitted a score
        const assoc = evInfo.associated || [];
        if (assoc.includes(tUid)) {
          const num = ev.event?.number;
          const name = ev.event?.name;
          const label = num ? `#${num}${name ? ' - ' + name : ''}` : (name || evUid);
          adjusted[tUid].affiliatedEventNames.push(label);
        }
      });

      adjusted[tUid].originalTotal = origTotal;
      adjusted[tUid].adjustedTotal = adjTotal;
      adjusted[tUid].affiliated = adjusted[tUid].affiliatedEventNames.length > 0;
    });

    // placements — compute both original and adjusted placements
    try {
      calculatePlacementsAsMap(adjusted, 'originalTotal', 'overallPlace');
      calculatePlacementsAsMap(adjusted, 'adjustedTotal', 'place');
    } catch (e) { console.error(e); }

    return adjusted;
  }, [eventsScores, teamsMapLocal, teamModes, affiliatedMode, manualOverrides, eventsInfo]);

  // Apply global affiliatedMode to teamModes when appropriate, storing which changes are auto-applied
  useEffect(() => {
    // Only act when affiliatedMode changes to average, max or none
    if (!affiliatedTeamUids || affiliatedTeamUids.length === 0) return;
    if (affiliatedMode === 'none') {
      // revert any auto-applied modes, but do not overwrite teams that were manually overridden
      setTeamModes(prev => {
        const next = { ...prev };
        const toRemove = [];
        Object.keys(autoAppliedModes).forEach(uid => {
          // skip restoring if the user manually overrode this team
          if (manualOverrides && manualOverrides[uid]) return;
          next[uid] = autoAppliedModes[uid];
          toRemove.push(uid);
        });
        if (toRemove.length === 0) return prev;
        setAutoAppliedModes(ap => {
          const copy = { ...ap };
          toRemove.forEach(u => delete copy[u]);
          return copy;
        });
        return next;
      });
      return;
    }

    // For average, max or exclude -> set affiliated teams to affiliated_* modes or exclude
    setTeamModes(prev => {
      const next = { ...prev };
      const newlyApplied = {};
      // Use the known teams list so keys exist for all teams; apply to affiliatedTeamUids only
      const allTeamUids = Object.keys(teamsMapLocal || teams || {});
      allTeamUids.forEach(uid => {
        if (!affiliatedTeamUids.includes(uid)) return;
        // If the user manually overrode this team's mode, skip auto-applying the global affiliated mode
        if (manualOverrides && manualOverrides[uid]) return;
        const currentMode = next[uid] || 'normal';
        const wasAuto = Boolean(autoAppliedModes && autoAppliedModes[uid] !== undefined);
        // Only override if team is in 'normal' or this team was previously auto-applied.
        if (currentMode === 'normal' || wasAuto) {
          // record previous mode only if not already recorded
          if (!wasAuto) {
            newlyApplied[uid] = currentMode;
          }
          if (affiliatedMode === 'average') {
            next[uid] = 'affiliated_average';
          } else if (affiliatedMode === 'max') {
            next[uid] = 'affiliated_max';
          } else if (affiliatedMode === 'exclude') {
            next[uid] = 'exclude';
          } else if (affiliatedMode === 'up_next') {
            next[uid] = 'affiliated_up_next';
          }
        }
      });
      if (Object.keys(newlyApplied).length > 0) {
        setAutoAppliedModes(ap => ({ ...ap, ...newlyApplied }));
      }
      return next;
    });
  }, [affiliatedMode, affiliatedTeamUids, teamsMapLocal, teams, manualOverrides]);

  const adjustedRows = useMemo(() => {
    return Object.values(adjustedTeamsMap || {}).map(t => ({
      id: t.uid,
      teamNumber: t.teamNumber,
      teamName: t.teamName,
      category: categoriesMap[t.category] || t.category,
      division: divisionsMap[t.division] || t.division,
      affiliated: t.affiliated,
      affiliatedEventNames: t.affiliatedEventNames || [],
      originalTotal: t.originalTotal,
      adjustedTotal: t.adjustedTotal,
      overallPlace: t.overallPlace,
      place: t.place,
      mode: t.mode
    }));
  }, [adjustedTeamsMap, categoriesMap, divisionsMap]);

  // byEventRows (for Table -> By Event)
  const byEventRows = useMemo(() => {
    if (!selectedEvent) return [];
    const event = eventsScores[selectedEvent];
    if (!event || !Array.isArray(event.scores)) return [];
    return event.scores.map((s, idx) => ({
      teamUid: s.teamUid,
      affiliated: (event.event?.associatedTeams || []).map(a => (typeof a === 'string' ? a : a.uid)).includes(s.teamUid),
      affiliatedEventNames: (event.event?.associatedTeams || []).map(a => (typeof a === 'string' ? a : a.uid) === s.teamUid ? event.event?.number ? `#${event.event.number}${event.event.name ? ' - ' + event.event.name : ''}${event.event.company ? ' - ' + event.event.company : ''}` : (event.event?.name || '') : null).filter(x=>x),
      id: `${selectedEvent || s.eventUid || ''}-${s.teamUid || ''}-${idx}`,
      teamNumber: s.team.teamNumber,
      teamName: s.team.teamName,
      category: categoriesMap[s.team.category] || '',
      division: divisionsMap[s.team.division] || '',
      // per-event raw and adjusted final scores
      finalScoreRaw: s.finalScore,
      finalScoreAdjusted: s.finalScoreAssociated ?? s.finalScore,
      categoryAvg: event.categoriesAvg?.[s.team.category] ?? '',
      overallAvg: event.avgOverall ?? '',
      categoryTop: event.categoryTop?.[s.team.category] ?? '',
      overallTop: event.overallTopInEvent ?? '',
      place: s.overallPlace,
      finalScoreAssociated: s.finalScoreAssociated,
      placeAssociated: s.overallPlaceAssociated,
    }))
    });

  // determine whether adjusted columns should be shown for this event
  const eventHasAdjustments = useMemo(() => {
    return (affiliatedMode && affiliatedMode !== 'none') || Object.values(teamModes || {}).some(m => m !== 'normal');
  }, [affiliatedMode, teamModes]);

  // missing rows (teams without a score for an event)
  const missingRows = useMemo(() => {
    // prefer local teams map which is set from firebase fetch; fallback to global teams
    let allTeamUids = Object.keys(teamsMapLocal && Object.keys(teamsMapLocal).length ? teamsMapLocal : teams || {});
    // If teams map is empty, derive a fallback list from submitted scores, configured events' associated teams, and eventsMap
    if (!allTeamUids || allTeamUids.length === 0) {
      const setU = new Set();

      // 1) collect any team UIDs from submitted scores
      sortEventUids(Object.keys(eventsScores || {})).forEach(evUid => {
        const ev = eventsScores[evUid];
        if (!ev) return;
        (ev.scores || []).forEach(s => { if (s && s.teamUid) setU.add(s.teamUid); });
        const assoc = ev.event?.associatedTeams || [];
        if (Array.isArray(assoc)) {
          assoc.forEach(a => setU.add(typeof a === 'string' ? a : a.uid));
        } else if (assoc && typeof assoc === 'object') {
          Object.keys(assoc).forEach(k => setU.add(typeof assoc[k] === 'string' ? assoc[k] : assoc[k].uid));
        }
      });

      // 2) collect team UIDs from configured events (eventsMap) associatedTeams
      sortEventUids(Object.keys(eventsMap || {})).forEach(evUid => {
        const ev = eventsMap[evUid];
        if (!ev) return;
        const assoc = ev?.associatedTeams || [];
        if (Array.isArray(assoc)) {
          assoc.forEach(a => setU.add(typeof a === 'string' ? a : a.uid));
        } else if (assoc && typeof assoc === 'object') {
          Object.keys(assoc).forEach(k => setU.add(typeof assoc[k] === 'string' ? assoc[k] : assoc[k].uid));
        }
      });

      allTeamUids = Array.from(setU);
    }

    // Iterate all configured events (from eventsMap). Fall back to eventsScores keys if eventsMap is empty.
    const rawEventUids = (eventsMap && Object.keys(eventsMap).length > 0) ? Object.keys(eventsMap) : Object.keys(eventsScores || {});
    const eventUids = sortEventUids(rawEventUids);

    // Removed high-frequency debug log to avoid console spam

    // Use sorted team list for expected teams so missingRows are ordered by teamNumber
    const expectedTeamUids = sortTeamUids(allTeamUids);

    return eventUids.reduce((acc, e) => {
      // prefer the populated event entry from eventsScores when available; otherwise create a stub with empty scores
      const eventEntry = (eventsScores && eventsScores[e]) ? eventsScores[e] : (eventsMap && eventsMap[e] ? { event: eventsMap[e], scores: [] } : null);
      if (!eventEntry) return acc;

      // build quick lookup map for this event: teamUid -> scoreObj
      const scoreByTeam = {};
      (eventEntry.scores || []).forEach(s => { if (s && s.teamUid) scoreByTeam[s.teamUid] = s; });

      // determine expected teams for this event: default to all teams (teamsMapLocal/teams)
      // Use the full teams list for expected teams so all teams appear as missing when no score is submitted
      // const expectedTeamUids = allTeamUids;

      expectedTeamUids.forEach((teamUid) => {
        const s = scoreByTeam[teamUid];
        // missing if no score submitted OR score exists but finalScore === 0 (and not a marker row)
        const isMissing = (!s) || (Number(s.finalScore) === 0 && !s.scoreMax);
        if (isMissing) {
          acc.push({
            id: `${e}-${teamUid}`,
            eventUid: e,
            eventNumber: eventEntry.event?.number,
            eventName: `${eventEntry.event?.name || ''} ${eventEntry.event?.company || ''}`.trim(),
            teamUid,
            teamNumber: (teamsMapLocal && teamsMapLocal[teamUid] && teamsMapLocal[teamUid].teamNumber) || teams[teamUid]?.teamNumber || '',
            teamName: (teamsMapLocal && teamsMapLocal[teamUid] && teamsMapLocal[teamUid].teamName) || teams[teamUid]?.teamName || '',
            affiliated: (eventEntry.event?.associatedTeams || []).map(a => (typeof a === 'string' ? a : a.uid)).includes(teamUid),
            // include rawScore if there is one (may be undefined)
            rawScore: s ? s.finalScore : undefined,
            scoreUid: s ? (s.scoreUid || s.uid) : undefined
          });
        }
      });

      return acc;
    }, []);
  }, [teams, teamsMapLocal, eventsScores, eventsMap]);

  // scores1500 rows (scores where finalScore === 1500, excluding scoreMax marker rows)
  const scores1500Rows = useMemo(() => {
    const evUids = sortEventUids(Object.keys(eventsScores || {}));
    return evUids.reduce((acc, e) => {
      const event = eventsScores[e];
      if (!event || !Array.isArray(event.scores)) return acc;
      event.scores.forEach((s, idx) => {
        if (!s) return;
        // Exclude marker rows where scoreMax === true
        if (s.scoreMax) return;
        if (Number(s.finalScore) === 1500) {
          const teamUid = s.teamUid;
          acc.push({
            id: `${e}-${teamUid}-${idx}`,
            eventUid: e,
            eventNumber: event.event?.number,
            eventName: `${event.event?.name || ''} ${event.event?.company || ''}`.trim(),
            teamUid,
            teamNumber: teams[teamUid]?.teamNumber || (s.team && s.team.teamNumber) || '',
            teamName: teams[teamUid]?.teamName || (s.team && s.team.teamName) || '',
            finalScore: s.finalScore,
            scoreUid: s.scoreUid || s.uid || '',
            updatedAt: s.updatedAt || s.createdAt || '',
            affiliated: (event.event?.associatedTeams || []).map(a => (typeof a === 'string' ? a : a.uid)).includes(teamUid)
          });
        }
      });
      return acc;
    }, []);
  }, [eventsScores, teams]);

  // debug logging to help diagnose missingRows/scores1500Rows
  const _resultsV2DebugPrevRef = useRef({ missingRowsLen: -1, scores1500Len: -1, eventsLen: -1, teamsLocalLen: -1 });
  useEffect(() => {
    try {
      const counts = {
        missingRowsLen: (missingRows || []).length,
        scores1500Len: (scores1500Rows || []).length,
        eventsLen: Object.keys(eventsScores || {}).length,
        teamsLocalLen: Object.keys(teamsMapLocal || {}).length
      };
      const prev = _resultsV2DebugPrevRef.current;
      const changed = counts.missingRowsLen !== prev.missingRowsLen || counts.scores1500Len !== prev.scores1500Len || counts.eventsLen !== prev.eventsLen || counts.teamsLocalLen !== prev.teamsLocalLen;
      if (changed && process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        _resultsV2DebugPrevRef.current = counts;
      }
    } catch (e) {}
  }, [missingRows, scores1500Rows, eventsScores, teamsMapLocal]);

  const groupedTopResults = useMemo(() => {
    const result = { competition: [], event: {}, category: {}, division: {} };

    const getTopByPlace = (rows, placeKey, topNVal, includeTiesVal) => {
      if (!topNVal || topNVal <= 0) return rows;
      const rowsWithPlace = rows.filter(r => Number.isFinite(r[placeKey]));
      if (rowsWithPlace.length === 0) return [];
      rowsWithPlace.sort((a,b) => a[placeKey] - b[placeKey]);
      if (rowsWithPlace.length <= topNVal) return rowsWithPlace;
      const nthPlace = rowsWithPlace[topNVal - 1][placeKey];
      if (includeTiesVal) return rowsWithPlace.filter(r => r[placeKey] <= nthPlace);
      return rowsWithPlace.slice(0, topNVal);
    };

    // competition-level (uses adjustedTeamsMap 'place')
    const compRows = Object.values(adjustedTeamsMap || {}).map(t => ({
      id: t.uid,
      uid: t.uid,
      teamNumber: t.teamNumber,
      teamName: t.teamName,
      category: categoriesMap[t.category] || t.category,
      division: divisionsMap[t.division] || t.division,
      affiliated: t.affiliated,
      affiliatedEventNames: t.affiliatedEventNames || [],
      originalTotal: t.originalTotal,
      adjustedTotal: t.adjustedTotal,
      overallPlace: t.overallPlace,
      place: t.place,
      mode: t.mode
    }));
    // return full competition list; slicing is handled by the UI component
    result.competition = getTopByPlace(compRows, 'place', 0, includeTies);

    // per-event
    sortEventUids(Object.keys(eventsScores || {})).forEach(evUid => {
      const ev = eventsScores[evUid];
      if (!ev || !Array.isArray(ev.scores)) { result.event[evUid] = []; return; }
        const evRows = ev.scores.map((s, idx) => ({
        id: `${evUid}-${s.teamUid}-${idx}`,
        teamUid: s.teamUid,
        teamNumber: s.team?.teamNumber || s.teamNumber,
        teamName: s.team?.teamName || s.teamName,
        category: categoriesMap[s.team?.category] || (s.team && categoriesMap[s.team?.category]) || '',
        division: divisionsMap[s.team?.division] || (s.team && divisionsMap[s.team?.division]) || '',
        affiliated: (ev.event?.associatedTeams || []).map(a => (typeof a === 'string' ? a : a.uid)).includes(s.teamUid),
        affiliatedEventNames: (ev.event?.associatedTeams || []).map(a => (typeof a === 'string' ? a : a.uid) === s.teamUid ? ev.event?.number ? `#${ev.event.number}${ev.event.name ? ' - ' + ev.event.name : ''}${ev.event.company ? ' - ' + ev.event.company : ''}` : (ev.event?.name || '') : null).filter(x=>x),
        finalScoreRaw: s.finalScore,
        finalScoreAssociated: s.finalScoreAssociated,
        place: s.overallPlace,
        placeAssociated: s.overallPlaceAssociated,
        categoryPlace: s.categoryPlace,
        categoryPlaceAssociated: s.categoryPlaceAssociated,
        divisionPlace: s.divisionPlace,
        divisionPlaceAssociated: s.divisionPlaceAssociated
      }));
      const placeKey = eventHasAdjustments ? 'placeAssociated' : 'place';
      // return full event rows; UI will slice based on local Top-N
      result.event[evUid] = getTopByPlace(evRows, placeKey, 0, includeTies);
    });

    // per-category (group adjustedTeamsMap by category)
    const catGroups = {};
    Object.values(adjustedTeamsMap || {}).forEach(t => {
      const key = t.category || 'Uncategorized';
      catGroups[key] = catGroups[key] || [];
      catGroups[key].push({
        id: t.uid,
        uid: t.uid,
        teamNumber: t.teamNumber,
        teamName: t.teamName,
        category: categoriesMap[t.category] || t.category,
        division: divisionsMap[t.division] || t.division,
        affiliated: t.affiliated,
        affiliatedEventNames: t.affiliatedEventNames || [],
        originalTotal: t.originalTotal,
        adjustedTotal: t.adjustedTotal,
        overallPlace: t.overallPlace,
        place: t.place,
        mode: t.mode
      });
    });
    Object.keys(catGroups).forEach(k => {
      // compute category-specific places based on adjustedTotal (lower is better)
      const group = catGroups[k];
      group.sort((a, b) => (Number(a.adjustedTotal) || 0) - (Number(b.adjustedTotal) || 0));
      let lastScore = null;
      let lastPlace = 0;
      for (let i = 0; i < group.length; i++) {
        const score = group[i].adjustedTotal;
        if (i === 0) {
          lastPlace = 1;
          group[i].categoryPlace = 1;
          lastScore = score;
        } else {
          if (score === lastScore) {
            group[i].categoryPlace = lastPlace;
          } else {
            group[i].categoryPlace = i + 1;
            lastPlace = i + 1;
            lastScore = score;
          }
        }
      }
      // return full category groups; UI will slice
      result.category[k] = getTopByPlace(group, 'categoryPlace', 0, includeTies);
    });

    // per-division
    const divGroups = {};
    Object.values(adjustedTeamsMap || {}).forEach(t => {
      const key = t.division || 'No Division';
      divGroups[key] = divGroups[key] || [];
      divGroups[key].push({
        id: t.uid,
        uid: t.uid,
        teamNumber: t.teamNumber,
        teamName: t.teamName,
        category: categoriesMap[t.category] || t.category,
        division: divisionsMap[t.division] || t.division,
        affiliated: t.affiliated,
        affiliatedEventNames: t.affiliatedEventNames || [],
        originalTotal: t.originalTotal,
        adjustedTotal: t.adjustedTotal,
        overallPlace: t.overallPlace,
        place: t.place,
        mode: t.mode
      });
    });
    Object.keys(divGroups).forEach(k => {
      // compute division-specific places based on adjustedTotal
      const group = divGroups[k];
      group.sort((a, b) => (Number(a.adjustedTotal) || 0) - (Number(b.adjustedTotal) || 0));
      let lastScoreD = null;
      let lastPlaceD = 0;
      for (let i = 0; i < group.length; i++) {
        const score = group[i].adjustedTotal;
        if (i === 0) {
          lastPlaceD = 1;
          group[i].divisionPlace = 1;
          lastScoreD = score;
        } else {
          if (score === lastScoreD) {
            group[i].divisionPlace = lastPlaceD;
          } else {
            group[i].divisionPlace = i + 1;
            lastPlaceD = i + 1;
            lastScoreD = score;
          }
        }
      }
      result.division[k] = getTopByPlace(group, 'divisionPlace', topN || 0, includeTies);
    });

    return result;
  }, [adjustedTeamsMap, eventsScores, topN, includeTies, categoriesMap, divisionsMap, eventHasAdjustments]);

  return {
    loading,
    eventsScores,
    teamsMapLocal,
    eventsMap,
    teams,
    teamModes,
    setTeamModes,
    affiliatedMode,
    setAffiliatedMode,
    autoAppliedModes,
    setAutoAppliedModes,
    manualOverrides,
    setManualOverrides,
    tabIndex,
    setTabIndex,
    reportType,
    setReportType,
    selectedEvent,
    setSelectedEvent,
    selectedTeam,
    setSelectedTeam,
    selectedCategory,
    setSelectedCategory,
    selectedDivision,
    setSelectedDivision,
    affiliatedTeamUids,
    categoriesMap,
    divisionsMap,
    adjustedTeamsMap,
    adjustedRows,
    byEventRows,
    eventHasAdjustments,
    missingRows,
    scores1500Rows,
    topN,
    setTopN,
    groupBy,
    setGroupBy,
    includeTies,
    setIncludeTies,
    groupedTopResults
  };
}
